import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import useFavorites from "@/hooks/useFavorites";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/base/Breadcrumb";
import DetailHero from "./components/DetailHero";
import CastList from "./components/CastList";
import RelatedMovies from "./components/RelatedMovies";

export default function Detail() {
  const { id = "" } = useParams();
  const movie = movies.find((m) => m.id === id);
  const { toggleMovie, movieIds } = useFavorites();
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState("");

  useSeo({
    title: movie
      ? `${movie.title}剧情介绍与演员信息 - 我的老婆姐姐`
      : "影视详情 - 我的老婆姐姐",
    description: movie
      ? `${movie.title}（${movie.year}）剧情简介、主演阵容与相关推荐。${movie.summary.slice(0, 60)}`
      : undefined,
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${movie.genres.join(",")},${movie.actors
          .map((a) => a.name)
          .join(",")},我的老婆姐姐`
      : undefined,
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，可粘贴分享");
    } catch {
      showToast("分享链接：" + window.location.href);
    }
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-5">
          <i className="ri-film-line"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被移除，或链接有误</p>
        <Link
          to="/"
          className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const fav = movieIds.includes(movie.id);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: movie.type, to: `/category/${movie.type === "电影" ? "movie" : "tv"}` },
          { label: movie.title },
        ]}
      />

      <div className="mt-5">
        <DetailHero
          movie={movie}
          isFav={fav}
          onToggleFav={() => toggleMovie(movie.id)}
          onShare={handleShare}
        />
      </div>

      <section className="mt-10">
        <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2 mb-4">
          <span className="w-1 h-5 rounded-full bg-accent-500"></span>
          剧情简介
        </h2>
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <p
            className={`text-sm md:text-base text-foreground-700 leading-relaxed ${
              expanded ? "" : "line-clamp-3"
            }`}
          >
            {movie.summary}
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {expanded ? "收起全文" : "展开全文"}
            <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
          <div className="mt-4 flex flex-wrap gap-2">
            {movie.tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
              >
                #{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CastList movie={movie} />
      <RelatedMovies movie={movie} />

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-secondary-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}